import {
  get,
  ref,
  query,
  orderByChild,
  equalTo,
  startAt,
  limitToLast,
  onValue,
  off
} from 'firebase/database';
import {
  ref as storageRef,
  deleteObject
} from 'firebase/storage';
import { database, storage } from './core';
import { Exercise, ExerciseType, ExercisePhoto, ExerciseFormData } from '../../types/exercise';
import logger from '../../utils/logger';
import { createWithKey, updateData, removeData, setData, uploadFile } from './api-wrapper.service';
import { cleanForFirebase } from '../../utils/firebase';
import { calculateImageAspectRatio } from '../../utils/image-helpers';
import { healthSyncService } from '../health/health-sync.service';
import { Capacitor } from '@capacitor/core';

/**
 * Get path to exercises for a user
 */
export const getExercisesPath = (userId: string) => `exercises/${userId}`;

/**
 * Get path to a specific exercise
 */
export const getExercisePath = (userId: string, exerciseId: string) =>
  `${getExercisesPath(userId)}/${exerciseId}`;

/**
 * Get path to exercise photos
 */
export const getExercisePhotosPath = (userId: string, exerciseId: string) =>
  `${getExercisePath(userId, exerciseId)}/photos`;

/**
 * Get storage path for exercise photos
 */
export const getExercisePhotosStoragePath = (userId: string, exerciseId: string) =>
  `photos/exercises/${userId}/${exerciseId}`;

/**
 * Helper: Convert photos from various formats to array for display
 * Original app stores photos as object with numeric keys: { "0": {...}, "1": {...} }
 * This function handles both formats for backwards compatibility
 */
export const normalizePhotosToArray = (photos: any): ExercisePhoto[] => {
  if (!photos) return [];

  // If already an array, return as-is
  if (Array.isArray(photos)) {
    return photos;
  }

  // If object with numeric keys, convert to array
  if (typeof photos === 'object') {
    return Object.keys(photos)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .map(key => photos[key])
      .filter(photo => photo && photo.url);
  }

  return [];
};

/**
 * Helper: Upload photos to exercise (matches original app pattern)
 * Original app saves photos as an OBJECT with numeric keys at /exercises/{userId}/{exerciseId}/photos: { "0": {...}, "1": {...} }
 */
const uploadPhotosToExercise = async (
  userId: string,
  exerciseId: string,
  photos: Array<{ file?: File; url?: string; aspectRatio?: number; caption?: string; }>
): Promise<void> => {
  const photosArray: Array<{ url: string; aspectRatio: number; caption: string; dateAdded: number; uri: null }> = [];

  for (const photo of photos) {
    let photoURL = photo.url;
    let aspectRatio = photo.aspectRatio || 1;

    // If it's a new photo (has file), upload it
    if (photo.file && photo.file.name && photo.file.size) {
      // Upload photo to Firebase Storage (matches original app storage path)
      const photoPath = `exercises/${userId}/${exerciseId}/photos/${Date.now()}_${photo.file.name}`;
      photoURL = await uploadFile(photoPath, photo.file);

      // Calculate aspect ratio if not provided
      if (!photo.aspectRatio) {
        try {
          aspectRatio = await calculateImageAspectRatio(photo.file);
        } catch (error) {
          logger.warn('Failed to calculate aspect ratio for exercise photo:', error);
        }
      }
    } else if (!photoURL) {
      // Skip if no file and no URL
      logger.warn('Skipping photo without file or URL');
      continue;
    }

    // Add to photos array (matches original app structure exactly)
    // Original app includes: url, aspectRatio, caption, dateAdded, uri: null
    photosArray.push({
      url: photoURL,
      aspectRatio: aspectRatio,
      caption: photo.caption || '',
      dateAdded: Date.now(),
      uri: null
    });
  }

  // Save photos as object with numeric keys (matches original app exactly)
  // Original app: { "0": {url, aspectRatio}, "1": {...} }
  if (photosArray.length > 0) {
    const photosObject: Record<string, { url: string; aspectRatio: number; caption: string; dateAdded: number; uri: null }> = {};
    photosArray.forEach((photo, index) => {
      photosObject[index.toString()] = photo;
    });
    await setData(`${getExercisePath(userId, exerciseId)}/photos`, photosObject);
  }
};

/**
 * Get a specific exercise by ID
 */
export const getExerciseById = async (ownerId: string, exerciseId: string): Promise<Exercise | null> => {
  try {
    const exerciseRef = ref(database, getExercisePath(ownerId, exerciseId));
    const snapshot = await get(exerciseRef);

    if (snapshot.exists()) {
      const data = snapshot.val();
      return {
        ...data,
        exerciseKey: exerciseId,
        author: ownerId
      } as Exercise;
    }

    return null;
  } catch (error) {
    logger.error('Error getting exercise:', error);
    throw error;
  }
};

/**
 * Get all exercises for a user
 */
export const getUserExercises = async (userId: string): Promise<Record<string, Exercise>> => {
  try {
    const exercisesRef = ref(database, getExercisesPath(userId));
    const snapshot = await get(exercisesRef);

    if (snapshot.exists()) {
      const exercises: Record<string, Exercise> = {};
      snapshot.forEach((childSnapshot) => {
        const exerciseKey = childSnapshot.key;
        if (exerciseKey) {
          exercises[exerciseKey] = {
            ...childSnapshot.val(),
            exerciseKey,
            author: userId
          };
        }
      });

      return exercises;
    }

    return {};
  } catch (error) {
    logger.error('Error getting user exercises:', error);
    throw error;
  }
};

/**
 * Get exercises for a user with optional filtering
 */
interface GetExercisesOptions {
  limit?: number;
  startDate?: number;
  type?: ExerciseType;
}

export const getExercises = async (
  userId: string,
  options: GetExercisesOptions = {}
): Promise<Exercise[]> => {
  try {
    const exercisesRef = ref(database, getExercisesPath(userId));

    let exercisesQuery = exercisesRef;

    // Apply query constraints based on options
    if (options.type) {
      exercisesQuery = query(
        exercisesRef,
        orderByChild('type'),
        equalTo(options.type)
      ) as any; // Type cast to avoid TypeScript errors
    } else {
      // Default sort by date descending (we'll reverse the results later)
      exercisesQuery = query(
        exercisesRef,
        orderByChild('date')
      ) as any; // Type cast to avoid TypeScript errors

      if (options.startDate) {
        exercisesQuery = query(
          exercisesQuery,
          startAt(options.startDate)
        ) as any; // Type cast to avoid TypeScript errors
      }

      if (options.limit) {
        exercisesQuery = query(
          exercisesQuery,
          limitToLast(options.limit)
        ) as any; // Type cast to avoid TypeScript errors
      }
    }

    const snapshot = await get(exercisesQuery);

    if (snapshot.exists()) {
      const exercises: Exercise[] = [];
      snapshot.forEach((childSnapshot) => {
        const exerciseData = childSnapshot.val();
        if (childSnapshot.key) {
          exercises.push({
            ...exerciseData,
            exerciseKey: childSnapshot.key,
            author: userId
          });
        }
      });

      // Sort by date descending
      return exercises.sort((a, b) => b.date - a.date);
    }

    return [];
  } catch (error) {
    logger.error('Error fetching exercises:', error);
    throw error;
  }
};

/**
 * Add a new exercise (raw exercise data)
 * Note: If photos need to be uploaded from files, use createExercise instead
 * This function expects photos to already be in the correct Record format
 */
export const addExercise = async (userId: string, exerciseData: Omit<Exercise, 'exerciseKey' | 'author'>): Promise<string> => {
  try {
    const timestamp = Date.now();
    const exercisesPath = getExercisesPath(userId);

    // Separate photos from base data (photos are stored separately)
    const { photos, ...baseData } = exerciseData;

    // STEP 1: Create exercise WITHOUT photos
    const exerciseKey = await createWithKey(exercisesPath, {
      ...cleanForFirebase(baseData),
      dateCreated: timestamp,
      dateModified: timestamp,
      author: userId
    });

    if (!exerciseKey) {
      throw new Error('Failed to generate exercise key');
    }

    // STEP 2: If photos exist (already in Record format), save them separately
    if (photos && Object.keys(photos).length > 0) {
      await setData(`${getExercisePath(userId, exerciseKey)}/photos`, photos);
    }

    return exerciseKey;
  } catch (error) {
    logger.error('Error adding exercise:', error);
    throw error;
  }
};

/**
 * Create a new exercise from form data
 * Follows original app pattern: create exercise first, then upload photos separately
 */
export const createExercise = async (userId: string, exerciseData: ExerciseFormData): Promise<string> => {
  try {
    const exercisesPath = getExercisesPath(userId);
    const timezoneOffset = exerciseData.date.getTimezoneOffset() / 60;
    const now = Date.now();

    // STEP 1: Create exercise WITHOUT photos (matches original app pattern)
    // Original app creates exercise first, gets key, then uploads photos
    const exerciseBase = {
      author: userId,
      date: exerciseData.date.getTime(),
      timezoneOffset,
      title: exerciseData.title || getTitleForTypeAndTime(exerciseData.date, exerciseData.type),
      type: exerciseData.type,
      duration: exerciseData.duration,
      location: exerciseData.location,
      note: exerciseData.note,
      taggedFriends: exerciseData.taggedFriends,
      // NO photos field here - uploaded separately
      dateCreated: now,
      dateModified: now
    };

    const exerciseKey = await createWithKey(exercisesPath, cleanForFirebase(exerciseBase));

    if (!exerciseKey) {
      throw new Error('Failed to generate exercise key');
    }

    // STEP 2: Upload photos AFTER exercise exists (matches original app pattern)
    if (exerciseData.photos && exerciseData.photos.length > 0) {
      try {
        await uploadPhotosToExercise(userId, exerciseKey, exerciseData.photos);
      } catch (photoError) {
        logger.error('Error uploading exercise photos:', photoError);
        // Don't fail exercise creation if photos fail
      }
    }

    // STEP 3: Sync to Apple Health (iOS only, if enabled)
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const isHealthEnabled = await healthSyncService.isProviderEnabled();
        if (isHealthEnabled) {
          logger.info(`Syncing exercise ${exerciseKey} to Apple Health`);

          // Build exercise object for sync
          const exerciseForSync = {
            exerciseKey,
            date: exerciseData.date.getTime(),
            duration: exerciseData.duration,
            type: exerciseData.type,
          };

          const syncSuccess = await healthSyncService.syncExercise(exerciseForSync);

          if (syncSuccess) {
            // Update exercise with health sync metadata (matches original app format)
            const providerId = healthSyncService.getCurrentProviderId();
            const healthMetadata = healthSyncService.makeHealthProvidersMetadata(
              {},
              { [providerId]: true }
            );

            await updateData(getExercisePath(userId, exerciseKey), healthMetadata);
            logger.info(`Exercise ${exerciseKey} synced to Apple Health successfully`);
          } else {
            logger.warn(`Failed to sync exercise ${exerciseKey} to Apple Health`);
          }
        }
      } catch (healthError) {
        // Log but don't fail the exercise creation if health sync fails
        logger.error('Error syncing exercise to Apple Health:', healthError);
      }
    }

    return exerciseKey;
  } catch (error) {
    logger.error('Error creating exercise:', error);
    throw error;
  }
};

/**
 * Update an existing exercise
 */
export const updateExercise = async (userId: string, exerciseId: string, updatedData: Partial<Exercise>): Promise<void> => {
  try {
    const exercisePath = getExercisePath(userId, exerciseId);
    // Clean updatedData to remove undefined values before writing to Firebase
    await updateData(exercisePath, {
      ...cleanForFirebase(updatedData),
      dateModified: Date.now()
    });
  } catch (error) {
    logger.error('Error updating exercise:', error);
    throw error;
  }
};

/**
 * Update an exercise from form data
 * Photos are handled separately: new photos uploaded, existing ones preserved
 */
export const updateExerciseFromForm = async (
  userId: string,
  exerciseId: string,
  exerciseData: Partial<ExerciseFormData>
): Promise<void> => {
  try {
    const exercisePath = getExercisePath(userId, exerciseId);

    const updates: Record<string, any> = {
      dateModified: Date.now()
    };

    if (exerciseData.title) updates.title = exerciseData.title;
    if (exerciseData.type) updates.type = exerciseData.type;
    if (exerciseData.date) updates.date = exerciseData.date.getTime();
    if (exerciseData.duration !== undefined) updates.duration = exerciseData.duration;
    if (exerciseData.location) updates.location = exerciseData.location;
    if (exerciseData.note !== undefined) updates.note = exerciseData.note;
    if (exerciseData.taggedFriends) updates.taggedFriends = exerciseData.taggedFriends;

    // Update base exercise data (NOT including photos)
    await updateData(exercisePath, updates);

    // Handle photos separately if provided
    if (exerciseData.photos && exerciseData.photos.length > 0) {
      try {
        await uploadPhotosToExercise(userId, exerciseId, exerciseData.photos);
      } catch (photoError) {
        logger.error('Error updating exercise photos:', photoError);
        // Don't fail exercise update if photos fail
      }
    }
  } catch (error) {
    logger.error('Error updating exercise:', error);
    throw error;
  }
};

/**
 * Delete an exercise
 */
export const deleteExercise = async (userId: string, exerciseId: string): Promise<void> => {
  try {
    const exercisePath = getExercisePath(userId, exerciseId);
    await removeData(exercisePath);
  } catch (error) {
    logger.error('Error deleting exercise:', error);
    throw error;
  }
};

// Like functionality removed - use the unified likes.service.ts instead

// Check if exercise is liked functionality removed - use the unified likes.service.ts instead

/**
 * Subscribe to an exercise for real-time updates
 */
export const subscribeToExercise = (
  userId: string,
  exerciseId: string,
  callback: (exercise: Exercise) => void
): (() => void) => {
  const exerciseRef = ref(database, getExercisePath(userId, exerciseId));

  onValue(exerciseRef, (snapshot) => {
    if (snapshot.exists()) {
      const exerciseData = snapshot.val();
      callback({
        ...exerciseData,
        exerciseKey: exerciseId,
        author: userId
      });
    }
  });

  // Return unsubscribe function
  return () => off(exerciseRef);
};

/**
 * Upload a photo for an exercise
 */
export const uploadExercisePhoto = async (
  userId: string,
  exerciseId: string,
  photoFile: File
): Promise<ExercisePhoto> => {
  try {
    // Generate a unique filename
    const fileName = `photo_${Date.now()}_${Math.random().toString(36).substring(2, 10)}.jpg`;
    const path = `${getExercisePhotosStoragePath(userId, exerciseId)}/${fileName}`;

    // Upload the photo using api-wrapper
    const downloadUrl = await uploadFile(path, photoFile);

    // Calculate aspect ratio
    let aspectRatio = 1;
    try {
      aspectRatio = await calculateImageAspectRatio(photoFile);
    } catch (error) {
      logger.warn('Failed to calculate aspect ratio:', error);
    }

    // Create photo object matching original app structure
    const photo: ExercisePhoto = {
      url: downloadUrl,
      aspectRatio,
      caption: '',
      dateAdded: Date.now()
    };

    return photo;
  } catch (error) {
    logger.error('Error uploading exercise photo:', error);
    throw error;
  }
};

/**
 * Delete a photo from an exercise
 */
export const deleteExercisePhoto = async (
  userId: string,
  exerciseId: string,
  photoId: string
): Promise<void> => {
  try {
    // Delete from storage
    const path = `${getExercisePhotosStoragePath(userId, exerciseId)}/${photoId}`;
    const photoRef = storageRef(storage, path);
    await deleteObject(photoRef);

    // Delete from database
    const photoPath = `${getExercisePhotosPath(userId, exerciseId)}/${photoId}`;
    await removeData(photoPath);
  } catch (error) {
    logger.error('Error deleting exercise photo:', error);
    throw error;
  }
};

/**
 * Generate a title for an exercise based on type and time of day
 */
const getTitleForTypeAndTime = (date: Date, type: ExerciseType = 'other'): string => {
  // Exercise type displayed to the user with first letter uppercase
  const displayType = type.charAt(0).toUpperCase() + type.slice(1);

  // Get hour in day according to local time
  const hourOfEvent = date.getHours(); // 0 - 23

  let prefix;

  // Assign a prefix based on the time of day
  if (hourOfEvent >= 4 && hourOfEvent < 12) {
    // Morning Exercise
    prefix = 'Morning';
  } else if (hourOfEvent >= 12 && hourOfEvent < 18) {
    // Afternoon Exercise
    prefix = 'Afternoon';
  } else {
    // Evening Exercise
    prefix = 'Evening';
  }

  return `${prefix} ${displayType}`;
};